import { GoogleMap, Marker, Circle, useJsApiLoader } from "@react-google-maps/api";
import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle, useContext, useMemo } from "react";
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getDocs, collection, addDoc, GeoPoint, serverTimestamp } from 'firebase/firestore';
import { geohashQueryBounds, distanceBetween, geohashForLocation } from 'geofire-common';
import { getNoteIcon, getUserIcon } from '../utils/noteIcons';
import NoteDetailsDialog from './NoteDetailsDialog';
import SearchAnimation from './SearchAnimation';
import RadarScan from './RadarScan';
import PapersSearchAnimation from './PapersSearchAnimation';
import NearMissGlow from './NearMissGlow';
import CelebrationAnimation from './CelebrationAnimation';
import FoundItemFlash from './FoundItemFlash';
import { AppContext } from '../AppContext';
import noteSearchService from '../services/noteSearchService';
import areaSpawnService from '../services/areaSpawnService';
import cardService from '../services/cardService';
import pointsService from '../services/pointsService';
import { getAuth } from 'firebase/auth';
import { Dialog, DialogContent, DialogActions, Button, Typography, Snackbar, Box } from '@mui/material';
import pinIcon from '../assets/note.png';
import foundSound from '../assets/audio/found.mp3';
import searchingSound from '../assets/audio/searching.mp3';
import pocketSound from '../assets/audio/pocket.mp3';

// Helper to remove emojis from note titles (for legacy notes)
const stripEmojis = (text) => {
  if (!text) return text;
  // Remove all emoji characters - comprehensive range including coin emoji
  return text.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, '').trim();
};

const Map = forwardRef((props, ref) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  const { isDarkMode } = useContext(AppContext);
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDragging, setIsDragging] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteDetailsOpen, setNoteDetailsOpen] = useState(false);
  const [searchAnimation, setSearchAnimation] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [shouldShowCircle, setShouldShowCircle] = useState(false);
  const circleInstancesRef = useRef([]);
  const isCleaningUpRef = useRef(false);
  const timerIdRef = useRef(null);
  const [prospectingAnimation, setProspectingAnimation] = useState(null);
  const [prospectingCooldown, setProspectingCooldown] = useState(false);
  const [prospectingToast, setProspectingToast] = useState({ open: false, message: '', type: 'normal' });
  const [nearMissGlow, setNearMissGlow] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [foundItemFlash, setFoundItemFlash] = useState(null);
  const [foundItemData, setFoundItemData] = useState(null); // Stores spawn data for pickup
  const [foundSnackbar, setFoundSnackbar] = useState({ open: false, message: '' });
  const foundAudioRef = useRef(null);
  const searchingAudioRef = useRef(null);
  const pocketAudioRef = useRef(null);
  const {
    onLocationChange,
    onDragged,
    onNotePickedUp,
    isPlacementMode,
    pinLocation,
    placementCenter,
    onPinLocationChange,
    pendingNoteData,
    onConfirmPlacement,
    radarActive,
    radarResult
  } = props;

  const userMarkerOptions = {
    position: center,
    icon: isLoaded ? getUserIcon() : undefined,
  };

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update circle visibility based on placement mode
  useEffect(() => {
    if (isPlacementMode && placementCenter) {
      isCleaningUpRef.current = false;
      setShouldShowCircle(true);
    } else if (!isPlacementMode && shouldShowCircle) {
      // Clean up ALL circle instances when exiting placement mode
      isCleaningUpRef.current = true;

      // Make a copy of the array before React unmounts the circles
      const circlesToClean = [...circleInstancesRef.current];

      // Remove all circles from the map
      circlesToClean.forEach(circle => {
        if (circle && circle.setMap) {
          circle.setMap(null);
        }
      });

      circleInstancesRef.current = [];
      setShouldShowCircle(false);

      setTimeout(() => {
        isCleaningUpRef.current = false;
      }, 100);
    }
  }, [isPlacementMode, placementCenter, shouldShowCircle]);

  // Cleanup all circles on component unmount
  useEffect(() => {
    return () => {
      circleInstancesRef.current.forEach(circle => {
        if (circle && circle.setMap) {
          circle.setMap(null);
        }
      });
      circleInstancesRef.current = [];
    };
  }, []);

  // Dark mode map styles
  const darkMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
    {
      featureType: "administrative.locality",
      elementType: "labels.text.fill",
      stylers: [{ color: "#d59563" }],
    },
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#263c3f" }],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{ color: "#6b9a76" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#38414e" }],
    },
    {
      featureType: "road",
      elementType: "geometry.stroke",
      stylers: [{ color: "#212a37" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{ color: "#9ca5b3" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#746855" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [{ color: "#1f2835" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [{ color: "#f3d19c" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "geometry",
      stylers: [{ color: "#2f3948" }],
    },
    {
      featureType: "transit.station",
      elementType: "labels.text.fill",
      stylers: [{ color: "#d59563" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#17263c" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#515c6d" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#17263c" }],
    },
  ];

  // Light mode map styles
  const lightMapStyles = [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ];

  const mapOptions = {
    styles: isDarkMode ? darkMapStyles : lightMapStyles,
    disableDefaultUI: true, // Removes all default UI controls
    zoomControl: false, // No zoom +/- buttons
    mapTypeControl: false, // No Map/Satellite toggle
    streetViewControl: false, // No Street View pegman
    fullscreenControl: false, // No fullscreen button
    gestureHandling: isPlacementMode ? 'none' : 'greedy', // Disable dragging in placement mode
    draggable: !isPlacementMode, // Disable dragging in placement mode
  };

  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCenter(currentLocation);
          onLocationChange(currentLocation);
          if (onDragged) {
            onDragged(false);
          }
        },
        (error) => {
          console.error(error);
        }
      );
    }
  }, [onLocationChange, onDragged]);

  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Fetch notes near the center location
  const fetchNearbyNotes = useCallback(async (location) => {
    if (!location) return;

    try {
      const db = firebase.firestore();
      const notesRef = db.collection('tokens');
      const radiusInM = 500; // Fetch notes within 500m
      const bounds = geohashQueryBounds([location.lat, location.lng], radiusInM);

      const allNotes = [];

      for (const b of bounds) {
        const q = notesRef
          .where('active', '==', true)
          .orderBy('location.geohash')
          .startAt(b[0])
          .endAt(b[1]);

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const noteLocation = data.location.geopoint;
          const distance = distanceBetween(
            [location.lat, location.lng],
            [noteLocation.latitude, noteLocation.longitude]
          );

          if (distance <= radiusInM / 1000) { // Convert to km
            // Filter out expired notes
            const expiresAt = data.expiresAt?.toDate();
            const isExpired = expiresAt && expiresAt < new Date();

            if (!isExpired) {
              allNotes.push({
                id: doc.id,
                ...data,
                position: {
                  lat: noteLocation.latitude,
                  lng: noteLocation.longitude,
                },
              });
            }
          }
        });
      }

      setNotes(allNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  }, []);

  // Update map styles when theme changes
  useEffect(() => {
    if (map) {
      map.setOptions(mapOptions);
    }
  }, [isDarkMode, map]);

  // Fetch notes when center changes
  useEffect(() => {
    if (isLoaded && center) {
      fetchNearbyNotes(center);
    }
  }, [center, isLoaded, fetchNearbyNotes]);

  const onLoad = useCallback((map) => {
    setMap(map);

    // Listen to dragstart and dragend events
    map.addListener("dragstart", () => {
      setIsDragging(true);
      clearInterval(timerIdRef.current);
    });

    // Update center state when dragging ends
    map.addListener("dragend", () => {
      setIsDragging(false);
      if (onDragged) {
        onDragged(true);
      }
      // Don't update center state - let it stay at user's GPS location
      // The map visual center and the center state can be different
    });
  }, [onLocationChange, onDragged]);

  // Run timer to update location every 10 seconds
  const runTimer = useCallback(() => {
    // Set timer to update location every 10 seconds
    timerIdRef.current = setInterval(() => {
      getCurrentLocation();
    }, 10000);
  }, []);

  useEffect(() => {
    runTimer(); // Run timer when component mounts
    // Clear interval when component unmounts
    return () => clearInterval(timerIdRef.current);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Handle picking up found item
  const handlePickUp = async () => {
    if (!foundItemData) return;

    const { spawn, clickedLocation, message } = foundItemData;
    const auth = getAuth();
    const userId = auth.currentUser?.uid;

    if (!userId) {
      setProspectingToast({
        open: true,
        message: 'Please log in to collect items',
        type: 'normal',
      });
      setFoundItemData(null);
      return;
    }

    // Claim the spawn
    const claimResult = await areaSpawnService.claimSpawn(spawn.id, userId, center);

    if (claimResult.success) {
      const claimedSpawn = claimResult.spawn;
      let successMessage = '';

      // Play pocket sound
      if (pocketAudioRef.current) {
        pocketAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
      }

      // Add to user's collection
      if (claimedSpawn.type === 'collectible') {
        // Add card to collection
        const cardResult = await cardService.addCardToCollection(userId, claimedSpawn.cardId);
        successMessage = `Collected ${cardResult.isNewCard ? 'NEW ' : ''}${claimedSpawn.rarity} Card: ${claimedSpawn.name}!`;
      } else if (claimedSpawn.type === 'tieredNote') {
        // Award points for tiered note
        await pointsService.awardTieredNotePoints(userId, claimedSpawn.tier);
        successMessage = `Collected ${claimedSpawn.tier} note worth ${claimedSpawn.pointValue} points!`;
      }

      // Close toast and show success message
      setProspectingToast({
        open: true,
        message: successMessage,
        type: 'normal',
      });

      // Refresh counts
      if (onNotePickedUp) {
        onNotePickedUp();
      }

      // Clear found item data
      setFoundItemData(null);
    } else {
      setProspectingToast({
        open: true,
        message: claimResult.error || 'Failed to claim item',
        type: 'normal',
      });
      setFoundItemData(null);
    }
  };

  // Handle map click for searching or pin placement
  const handleMapClick = async (e) => {
    // Don't do anything if clicking on a marker
    if (e.placeId) return;

    const clickedLat = e.latLng.lat();
    const clickedLng = e.latLng.lng();
    const clickedLocation = { lat: clickedLat, lng: clickedLng };

    // If in placement mode, move the pin
    if (isPlacementMode) {
      // Check if within 100m radius
      const distance = distanceBetween(
        [placementCenter.lat, placementCenter.lng],
        [clickedLat, clickedLng]
      );
      if (distance <= 0.1) { // 100m = 0.1km
        onPinLocationChange(clickedLocation);
      }
      return;
    }

    // Default mode: Prospecting for hidden treasures
    // Check cooldown
    if (prospectingCooldown) {
      return;
    }

    // Show prospecting animation and play searching sound
    setProspectingAnimation(clickedLocation);
    if (searchingAudioRef.current) {
      searchingAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }

    // Perform prospecting
    const result = await areaSpawnService.prospectArea(clickedLocation, center);

    // Clear animation after 2 seconds
    setTimeout(() => {
      setProspectingAnimation(null);
    }, 2000);

    if (result.tooFar) {
      // Out of range - show toast after animation
      setTimeout(() => {
        setProspectingToast({
          open: true,
          message: result.message,
          type: 'normal',
        });
      }, 2000);
      return;
    }

    if (result.found) {
      // Found an item!
      const auth = getAuth();
      const userId = auth.currentUser?.uid;

      if (!userId) {
        setTimeout(() => {
          setProspectingToast({
            open: true,
            message: 'Please log in to collect items',
            type: 'normal',
          });
        }, 2000);
        return;
      }

      const spawn = result.spawn;
      let message = '';

      // Generate preview message
      if (spawn.type === 'collectible') {
        message = `${spawn.rarity} Card: ${spawn.name}`;
      } else if (spawn.type === 'tieredNote') {
        message = `${spawn.tier} note worth ${spawn.pointValue} points`;
      }

      // Show flash, celebration animation, play sound, and show persistent toast after search animation
      setTimeout(() => {
        // Flash the item icon
        setFoundItemFlash({
          location: clickedLocation,
          type: spawn.type
        });

        // Start celebration (fireworks + glitter)
        setCelebration(clickedLocation);

        if (foundAudioRef.current) {
          foundAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
        }

        // Store spawn data and show persistent toast with Pick Up button
        setFoundItemData({
          spawn,
          clickedLocation,
          message,
        });

        setProspectingToast({
          open: true,
          message,
          type: 'found',
        });
      }, 2000);

      // Enable cooldown
      setProspectingCooldown(true);
      setTimeout(() => {
        setProspectingCooldown(false);
      }, 2000);
    } else if (result.nearMiss) {
      // Something nearby but didn't find it - show glow and toast after animation
      setTimeout(() => {
        // Show yellow glow with question mark
        setNearMissGlow(clickedLocation);
        // Show toast
        setProspectingToast({
          open: true,
          message: result.message,
          type: 'normal',
        });
      }, 2000);

      // Enable cooldown
      setProspectingCooldown(true);
      setTimeout(() => {
        setProspectingCooldown(false);
      }, 2000);
    } else if (result.nothingFound) {
      // Nothing in the area - show toast after animation
      setTimeout(() => {
        setProspectingToast({
          open: true,
          message: result.message,
          type: 'normal',
        });
      }, 2000);

      // Enable cooldown
      setProspectingCooldown(true);
      setTimeout(() => {
        setProspectingCooldown(false);
      }, 2000);
    }
  };

  const handleAnimationComplete = () => {
    setSearchAnimation(null);
  };

  // Handle confirming note drop
  useEffect(() => {
    const dropNote = async () => {
      if (!isPlacementMode || !pendingNoteData || !pinLocation) return;

      // Check if we're being called because onConfirmPlacement was triggered
      // We'll listen for this via a separate effect
    };
  }, [isPlacementMode, pendingNoteData, pinLocation]);

  // Listen for confirmation trigger
  const confirmPlacementRef = useRef(onConfirmPlacement);
  useEffect(() => {
    confirmPlacementRef.current = onConfirmPlacement;
  }, [onConfirmPlacement]);

  useImperativeHandle(ref, () => ({
    getCurrentLocation,
    getMapCenter: () => {
      if (map) {
        const mapCenter = map.getCenter();
        return { lat: mapCenter.lat(), lng: mapCenter.lng() };
      }
      return center;
    },
    dropNote: async () => {
      if (!pendingNoteData || !pinLocation) {
        return false;
      }

      try {
        const auth = getAuth();
        const db = firebase.firestore();

        // Deduct points
        const success = await pointsService.spendPoints(
          auth.currentUser.uid,
          pendingNoteData.pointsCost,
          `Placed note for ${pendingNoteData.expiryHours} hours`
        );

        if (!success) {
          alert(`Not enough points! You need ${pendingNoteData.pointsCost} points.`);
          return false;
        }

        // Calculate expiry
        const expiryDate = new Date();
        expiryDate.setHours(expiryDate.getHours() + pendingNoteData.expiryHours);

        // Use first 50 chars as title
        const title = pendingNoteData.content.substring(0, 50);
        const description = pendingNoteData.content.length > 50
          ? pendingNoteData.content.substring(50)
          : pendingNoteData.content;

        const tokenData = {
          title: title || 'Untitled Note',
          description,
          active: true,
          expiresAt: expiryDate,
          batchId: null,
          location: {
            geopoint: new GeoPoint(pinLocation.lat, pinLocation.lng),
            geohash: geohashForLocation([pinLocation.lat, pinLocation.lng]),
            address: '',
            placeName: '',
          },
          createdBy: auth.currentUser.uid,
          createdAt: serverTimestamp(),
          currentOwner: auth.currentUser.uid,
          currentHolder: import.meta.env.VITE_EARTH_MOTHER,
          lastTransferTime: serverTimestamp(),
          tokenType: 'note',
          noteType: 'static',
          updatedAt: serverTimestamp(),
          isPublic: true,
          tags: [],
        };

        const tokensCollection = collection(db, 'tokens');
        await addDoc(tokensCollection, tokenData);

        // Increment notes placed counter
        await pointsService.incrementNotesPlaced(auth.currentUser.uid);

        // Refresh notes on map
        fetchNearbyNotes(center);

        return true;
      } catch (error) {
        console.error('Error dropping note:', error);
        alert('Failed to drop note. Please try again.');
        return false;
      }
    }
  }));

  return isLoaded ? (
    <div style={{ height: "100vh", position: "relative" }}>
      <GoogleMap
        mapContainerStyle={{ height: "100%" }}
        center={center}
        zoom={isMobile ? 17 : 18}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={mapOptions}
      >
        {/* User location marker */}
        <Marker options={userMarkerOptions} />

        {/* Placement mode overlay */}
        {shouldShowCircle && placementCenter && (
          <>
            {/* Placement radius circle */}
            <Circle
              center={placementCenter}
              radius={100}
              options={{
                fillColor: '#2196F3',
                fillOpacity: 0.15,
                strokeColor: '#2196F3',
                strokeOpacity: 0.5,
                strokeWeight: 2,
                clickable: false,
              }}
              onLoad={(circle) => {
                // Track ALL circle instances (React StrictMode creates duplicates)
                circleInstancesRef.current.push(circle);

                // Hide all but the first circle to prevent visual duplicates
                if (circleInstancesRef.current.length > 1) {
                  circle.setVisible(false);
                }
              }}
              onUnmount={(circle) => {
                // Only remove from array if we're not in cleanup mode
                // (during cleanup we need the array intact to remove all circles)
                if (!isCleaningUpRef.current) {
                  circleInstancesRef.current = circleInstancesRef.current.filter(c => c !== circle);
                }
              }}
            />
            {/* Pin marker */}
            {pinLocation && (
              <Marker
                position={pinLocation}
                icon={{
                  url: pinIcon,
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 40),
                }}
                draggable={true}
                onDragEnd={(e) => {
                  const newLat = e.latLng.lat();
                  const newLng = e.latLng.lng();
                  const newLocation = { lat: newLat, lng: newLng };

                  // Check if within radius (100m = 0.1km)
                  const distance = distanceBetween(
                    [placementCenter.lat, placementCenter.lng],
                    [newLat, newLng]
                  );
                  if (distance <= 0.1) {
                    onPinLocationChange(newLocation);
                  } else {
                    // Snap back to previous location if out of bounds
                    onPinLocationChange(pinLocation);
                  }
                }}
              />
            )}
          </>
        )}

        {/* Note markers - hide in placement mode */}
        {!isPlacementMode && notes.map((note) => (
          <Marker
            key={note.id}
            position={note.position}
            icon={getNoteIcon(note.noteType || 'static', note.noteTier)}
            title={stripEmojis(note.title)}
            onClick={() => {
              setSelectedNote(note);
              setNoteDetailsOpen(true);
            }}
          />
        ))}
      </GoogleMap>

      {/* Search Animation */}
      {searchAnimation && (
        <SearchAnimation
          position={searchAnimation.position}
          result={searchAnimation.result}
          onComplete={handleAnimationComplete}
        />
      )}

      {/* Radar Scan Animation */}
      {radarActive && map && (
        <RadarScan
          center={center}
          active={radarActive}
          itemsDetected={radarResult?.itemsDetected || false}
          mapRef={{ getMap: () => map }}
        />
      )}

      {/* Papers Search Animation */}
      {prospectingAnimation && map && (
        <PapersSearchAnimation
          location={prospectingAnimation}
          active={true}
          mapRef={{ getMap: () => map }}
        />
      )}

      {/* Near Miss Glow */}
      {nearMissGlow && map && (
        <NearMissGlow
          location={nearMissGlow}
          active={true}
          mapRef={{ getMap: () => map }}
          onComplete={() => setNearMissGlow(null)}
        />
      )}

      {/* Celebration Animation */}
      {celebration && map && (
        <CelebrationAnimation
          location={celebration}
          active={true}
          mapRef={{ getMap: () => map }}
          onComplete={() => setCelebration(null)}
        />
      )}

      {/* Found Item Flash */}
      {foundItemFlash && map && (
        <FoundItemFlash
          location={foundItemFlash.location}
          itemType={foundItemFlash.type}
          active={true}
          mapRef={{ getMap: () => map }}
          onComplete={() => setFoundItemFlash(null)}
        />
      )}

      {/* Prospecting Toast */}
      <Snackbar
        open={prospectingToast.open}
        autoHideDuration={prospectingToast.type === 'found' ? null : 4000}
        onClose={() => {
          if (prospectingToast.type !== 'found') {
            setProspectingToast({ open: false, message: '', type: 'normal' });
          }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '80px !important',
        }}
      >
        <Box
          sx={{
            backgroundColor: prospectingToast.type === 'found'
              ? 'rgba(255, 193, 7, 0.95)'
              : 'rgba(0, 0, 0, 0.95)',
            color: prospectingToast.type === 'found' ? '#000' : 'white',
            padding: '16px 28px',
            borderRadius: '20px',
            boxShadow: prospectingToast.type === 'found'
              ? '0 8px 32px rgba(255, 193, 7, 0.5)'
              : '0 8px 32px rgba(0, 0, 0, 0.5)',
            border: prospectingToast.type === 'found'
              ? '2px solid rgba(255, 193, 7, 0.8)'
              : '2px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: 'Gochi Hand, cursive',
              textAlign: 'center',
            }}
          >
            {prospectingToast.message}
          </Typography>

          {/* Pick Up button for found items */}
          {prospectingToast.type === 'found' && foundItemData && (
            <Button
              variant="contained"
              onClick={handlePickUp}
              sx={{
                backgroundColor: '#000',
                color: '#FFD700',
                fontFamily: 'Gochi Hand, cursive',
                fontWeight: 700,
                fontSize: '1rem',
                padding: '8px 24px',
                borderRadius: '12px',
                textTransform: 'none',
                border: '2px solid #FFD700',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: '#1a1a1a',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Pick Up
            </Button>
          )}
        </Box>
      </Snackbar>

      {/* Hidden audio elements */}
      <audio ref={foundAudioRef} src={foundSound} preload="auto" />
      <audio ref={searchingAudioRef} src={searchingSound} preload="auto" />
      <audio ref={pocketAudioRef} src={pocketSound} preload="auto" />

      <NoteDetailsDialog
        open={noteDetailsOpen}
        note={selectedNote}
        onClose={() => {
          setNoteDetailsOpen(false);
          setSelectedNote(null);
        }}
        onNoteUpdated={() => {
          // Refresh notes after pickup
          fetchNearbyNotes(center);
          // Notify parent component to refresh counts and points
          if (onNotePickedUp) {
            onNotePickedUp();
          }
        }}
        onShowSuccess={(message) => {
          setSuccessMessage(message);
          setSuccessDialogOpen(true);
        }}
      />

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)}>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" sx={{ fontFamily: 'Gochi Hand, cursive' }}>
            {successMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessDialogOpen(false)} variant="contained" fullWidth>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  ) : (
    <></>
  );
});

export default Map;
